import { ChevronRight } from 'lucide-react'
import {
	ContentPreviewSheet,
	type SheetStackEntry,
	useSheetNavigation,
} from '#app/routes/lignes/+shared/sheet-navigation.tsx'
import { getCategoryColorHex } from '#app/utils/category-colors.ts'
import { useRelatedContentEnabled } from '#app/utils/user.ts'

interface LignesDuJourHighlightItem {
	id: string
	contentHtml: string
	excerpt: string
	content: {
		id: string
		title: string
		slug: string
		contentType: string
		createdAt: Date | string
		categories: Array<{ id: string; name: string; slug: string; color: string }>
	}
}

interface LignesDuJourHighlightProps {
	publishedAt: Date
	items: LignesDuJourHighlightItem[]
}

function toStackEntry(item: LignesDuJourHighlightItem): SheetStackEntry {
	return {
		id: item.content.id,
		title: item.content.title,
		slug: item.content.slug,
		contentType: item.content.contentType,
		contentHtml: item.contentHtml,
		categories: item.content.categories.map((c) => ({
			name: c.name,
			color: c.color,
		})),
		relations: [],
	}
}

export function LignesDuJourHighlight({
	publishedAt,
	items,
}: LignesDuJourHighlightProps) {
	const sheetNav = useSheetNavigation()
	const relatedContentEnabled = useRelatedContentEnabled()

	if (items.length === 0) return null

	const formattedDate = new Intl.DateTimeFormat('fr-CA', {
		day: 'numeric',
		month: 'long',
	}).format(new Date(publishedAt))

	return (
		<section className="border-border/60 full-bleed mb-8 border-y py-5 md:mb-10">
			<header className="mb-4 flex items-start justify-between gap-4 px-4 md:px-0">
				<div>
					<p className="text-plq-rouge flex items-center gap-2 text-[11px] font-semibold tracking-[0.15em] uppercase">
						<span
							className="bg-plq-rouge h-1.5 w-1.5 animate-pulse rounded-full"
							aria-hidden="true"
						/>
						À la une
					</p>
					<h2 className="mt-1.5 flex items-center gap-2.5 text-xl font-bold md:text-2xl">
						Lignes du jour
						<span className="bg-muted text-muted-foreground inline-flex h-6 min-w-6 items-center justify-center rounded-full px-2 text-xs font-bold">
							{items.length}
						</span>
					</h2>
				</div>
				<span className="text-muted-foreground bg-muted mt-1 shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium">
					{formattedDate}
				</span>
			</header>

			<ul className="divide-border/60 divide-y">
				{items.map((item) => (
					<li key={item.id}>
						<button
							type="button"
							className="group active:bg-muted/60 w-full cursor-pointer px-4 py-4 text-left transition-colors md:px-0"
							onClick={() => {
								sheetNav.open(toStackEntry(item))
								// enrichCurrent only backfills related content — skip the
								// round trip entirely when the feature is off.
								if (relatedContentEnabled) {
									void sheetNav.enrichCurrent(item.content.slug)
								}
							}}
						>
							<div className="flex items-center gap-2">
								{item.content.categories.map((cat) => {
									const hex = getCategoryColorHex(cat.color)
									return (
										<span
											key={cat.id}
											className="inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide uppercase"
											style={{
												backgroundColor: `${hex}20`,
												color: hex,
											}}
										>
											{cat.name}
										</span>
									)
								})}
							</div>
							<h3 className="mt-2 text-base font-semibold">
								<span className="group-hover:text-primary transition-colors">
									{item.content.title}
								</span>
							</h3>
							<p className="text-muted-foreground mt-2 line-clamp-2 text-sm">
								{item.excerpt}
							</p>
							<span className="text-primary mt-2 inline-flex items-center gap-1 text-xs font-semibold">
								Lire la suite
								<ChevronRight
									className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
									aria-hidden="true"
								/>
							</span>
						</button>
					</li>
				))}
			</ul>

			<ContentPreviewSheet sheetNav={sheetNav} />
		</section>
	)
}
